import React, { useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { usePagedQuery } from "../hooks/usePagedQuery";
import { useAvailableDashboards } from "../hooks/useAvailableDashboards";
import type { AuthUser, Notification } from "@meupedaco/shared";

type NavItem = { to: string; label: string; permission?: string | string[] };
type NavGroup = { label: string; items: NavItem[] };

const navGroups: NavGroup[] = [
  {
    label: "Dashboards",
    items: [
      {
        to: "/dash/exec",
        label: "Executivo",
        permission: "DASHBOARD_FINANCEIRO_GERAL",
      },
      {
        to: "/dash/ops",
        label: "Operacional",
        permission: "DASHBOARD_CAIXA",
      },
      {
        to: "/dash/gov",
        label: "Governanca",
        permission: "DASHBOARD_FINANCEIRO_GERAL",
      },
      {
        to: "/dash/indices",
        label: "Indices",
        permission: "DASHBOARD_FINANCEIRO_GERAL",
      },
      {
        to: "/dash/pagamentos",
        label: "Cartoes",
        permission: "DASHBOARD_CARTOES",
      },
      {
        to: "/dash/orcamento",
        label: "Orcamento",
        permission: "DASHBOARD_ORCAMENTO",
      },
      {
        to: "/governanca/tarefas/dashboard",
        label: "Dashboard de Tarefas",
        permission: "TASK_DASHBOARD_VIEW",
      },
    ],
  },
  {
    label: "Financeiro",
    items: [
      { to: "/financeiro/contas-pagar", label: "Contas a Pagar" },
      { to: "/financeiro/contas-receber", label: "Contas a Receber" },
      { to: "/financeiro/movimentacoes", label: "Movimentacoes" },
      { to: "/financeiro/transferencias", label: "Transferencias" },
      { to: "/financeiro/fluxo-caixa", label: "Fluxo de Caixa" },
      { to: "/financeiro/orcamentos", label: "Orcamentos" },
      { to: "/financeiro/boletos", label: "Boletos" },
      { to: "/financeiro/boletos/importar", label: "Importar Boleto" },
      { to: "/financeiro/cartoes", label: "Cartoes" },
      { to: "/financeiro/faturas", label: "Faturas" },
      {
        to: "/financeiro/cartoes/recorrentes",
        label: "Recorrentes do cartao",
        permission: "FIN_CREDITCARD_RECURRING_VIEW",
      },
      {
        to: "/financeiro/cartoes/reconciliacao",
        label: "Reconciliacao Cartao",
        permission: "FIN_CC_RECONCILE_VIEW",
      },
      {
        to: "/financeiro/importar-meta",
        label: "Importar Meta",
        permission: ["FIN_META_IMPORT_VIEW", "FIN_META_IMPORT_CREATE"],
      },
      { to: "/financeiro/importar-ofx", label: "Importar OFX" },
    ],
  },
  {
    label: "Cadastros",
    items: [
      { to: "/cadastros/entidades", label: "Entidades" },
      { to: "/cadastros/fornecedores", label: "Fornecedores" },
      { to: "/cadastros/clientes", label: "Clientes" },
      { to: "/cadastros/bancos", label: "Bancos" },
      { to: "/cadastros/contas-bancarias", label: "Contas Bancarias" },
      { to: "/cadastros/centros-custo", label: "Centros de Custo" },
      { to: "/cadastros/plano-contas", label: "Plano de Contas" },
      { to: "/cadastros/categorias", label: "Categorias" },
      { to: "/cadastros/categorias-opex", label: "Categorias OPEX" },
      { to: "/cadastros/bandeiras-cartao", label: "Bandeiras de Cartao" },
    ],
  },
  {
    label: "Pessoas",
    items: [
      { to: "/pessoas/socios", label: "Socios" },
      { to: "/pessoas/funcionarios", label: "Funcionarios" },
      { to: "/pessoas/funcoes", label: "Funcoes" },
    ],
  },
  {
    label: "Integracoes",
    items: [
      { to: "/financeiro/openfinance-conexoes", label: "Open Finance - Conexoes" },
      { to: "/financeiro/openfinance-reconciliacao", label: "Open Finance - Conciliacao" },
      { to: "/financeiro/openfinance-sync", label: "Open Finance - Sync Runs" },
    ],
  },
  {
    label: "Governanca",
    items: [
      { to: "/governanca/tarefas", label: "Tarefas" },
      { to: "/governanca/tarefas/configuracoes", label: "Config. de Tarefas" },
      { to: "/governanca/recorrencias", label: "Recorrencias" },
      { to: "/governanca/calendario", label: "Calendario" },
      { to: "/auditoria/logs", label: "Auditoria" },
      {
        to: "/admin/validacoes-financeiras",
        label: "Validacoes financeiras",
        permission: "AUDIT_READ",
      },
    ],
  },
  {
    label: "Relatorios",
    items: [
      { to: "/relatorios/orcado-realizado", label: "Orcado vs Realizado" },
      { to: "/relatorios/necessidade-caixa", label: "Necessidade de Caixa" },
      { to: "/relatorios/orcamento", label: "Orcamento" },
      { to: "/relatorios/dre", label: "DRE" },
      { to: "/relatorios/balanco", label: "Balanco Patrimonial" },
      { to: "/relatorios/dfc", label: "Fluxo de Caixa (DFC)" },
    ],
  },
  {
    label: "Configuracoes",
    items: [
      { to: "/financeiro/contratos", label: "Contratos" },
      { to: "/financeiro/renovacoes", label: "Renovacoes" },
      { to: "/indices", label: "Indices Economicos" },
      { to: "/configuracoes/usuarios", label: "Usuarios" },
      { to: "/configuracoes/perfis-acesso", label: "Perfis de Acesso" },
      { to: "/payment-methods", label: "Formas de Pagamento" },
    ],
  },
];

export const AppLayout: React.FC = () => {
  const { user, logout, client, updateUser } = useAuth();
  const { data: availableDashboards } = useAvailableDashboards();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const notificationsQuery = usePagedQuery<Notification>({
    endpoint: "/notifications",
    filters: { unreadOnly: false },
    initialTake: 10,
    orderBy: "createdAt",
    orderDir: "desc",
  });
  const unreadQuery = usePagedQuery<Notification>({
    endpoint: "/notifications",
    filters: { unreadOnly: true },
    initialTake: 1,
  });
  const initials = user?.name
    ? user.name
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "MP";
  const title = user?.title ?? "Usuario";
  const dashboardPermissions = useMemo(
    () => new Set(availableDashboards),
    [availableDashboards]
  );
  const userPermissions = useMemo(
    () => new Set(user?.permissions ?? []),
    [user?.permissions]
  );
  const filteredNavGroups = useMemo(() => {
    return navGroups
      .map((group) => {
        const items = group.items.filter((item) => {
          if (!item.permission) {
            return true;
          }
          const required = Array.isArray(item.permission)
            ? item.permission
            : [item.permission];
          if (group.label === "Dashboards") {
            return required.some((perm) => dashboardPermissions.has(perm));
          }
          return required.some((perm) => userPermissions.has(perm));
        });
        return { ...group, items };
      })
      .filter((group) => group.items.length > 0);
  }, [dashboardPermissions, userPermissions]);

  const markAsRead = async (item: Notification) => {
    if (item.readAt) {
      return;
    }
    await client.post(`/notifications/${item.id}/read`, {});
    await notificationsQuery.reload();
    await unreadQuery.reload();
  };

  const markAllRead = async () => {
    await client.post("/notifications/read-all", {});
    await notificationsQuery.reload();
    await unreadQuery.reload();
  };

  const handleAvatarClick = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!user) {
      return;
    }
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const result = reader.result;
      if (typeof result !== "string") {
        return;
      }
      const response = await client.put("/users/me", {
        email: user?.email,
        phone: user?.phone ?? null,
        photoUrl: result,
      });
      if (!response.ok) {
        alert("Falha ao atualizar foto.");
        return;
      }
      const payload = (await response.json()) as { data: AuthUser };
      updateUser(payload.data);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="d-flex min-vh-100 bg-light">
      <aside className="border-end bg-white" style={{ width: 260 }}>
        <div className="p-3 border-bottom">
          <Link to="/" className="d-inline-block">
            <img
              src="https://www.meupedaco.com.br/assets/MeuPedaco-EY1WI6D1.png"
              alt="MeuPedaco"
              style={{ height: 36 }}
            />
          </Link>
        </div>
        <div className="p-3 d-flex flex-column gap-4">
          {filteredNavGroups.map((group) => (
            <div key={group.label}>
              <div className="text-uppercase small fw-semibold text-muted mb-2">
                {group.label}
              </div>
              <div className="d-flex flex-column gap-2">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `text-decoration-none small ${
                        isActive ? "text-primary fw-semibold" : "text-muted"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>
      </aside>
      <div className="flex-grow-1">
        <header className="bg-white border-bottom">
          <div className="container py-3 d-flex align-items-center justify-content-end gap-3">
            <Link to="/" className="btn btn-outline-primary">
              Inicio
            </Link>
            <div className="dropdown">
              <button
                className="btn btn-outline-secondary position-relative"
                type="button"
                onClick={() => setNotifOpen((prev) => !prev)}
              >
                Notificacoes
                {unreadQuery.total > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {unreadQuery.total}
                  </span>
                )}
              </button>
              <div className={`dropdown-menu dropdown-menu-end ${notifOpen ? "show" : ""}`}>
                <div className="px-3 py-2 border-bottom">
                  <strong>Notificacoes</strong>
                </div>
                <div style={{ maxHeight: 240, overflowY: "auto" }}>
                  {notificationsQuery.rows.map((item) => (
                    <button
                      key={item.id}
                      className={`dropdown-item text-wrap ${item.readAt ? "text-muted" : ""}`}
                      type="button"
                      onClick={() => markAsRead(item)}
                    >
                      <div className="fw-semibold">{item.title}</div>
                      <small className="text-muted">{item.message ?? "-"}</small>
                    </button>
                  ))}
                  {notificationsQuery.rows.length === 0 && (
                    <div className="dropdown-item text-muted">
                      Nenhuma notificacao.
                    </div>
                  )}
                </div>
                <div className="dropdown-divider" />
                <button className="dropdown-item" type="button" onClick={markAllRead}>
                  Marcar todas como lidas
                </button>
              </div>
            </div>

            <div className="dropdown">
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={handleAvatarChange}
              />
              <button
                className="btn btn-link text-decoration-none d-flex align-items-center gap-2"
                type="button"
                onClick={() => setProfileOpen((prev) => !prev)}
              >
                <div onClick={handleAvatarClick} role="presentation">
                  {user?.photoUrl ? (
                    <img
                      src={user.photoUrl}
                      alt="Foto"
                      className="rounded-circle"
                      style={{ width: 36, height: 36, objectFit: "cover" }}
                    />
                  ) : (
                    <div
                      className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                      style={{ width: 36, height: 36 }}
                    >
                      {initials}
                    </div>
                  )}
                </div>
                <div className="text-end">
                  <div className="fw-semibold">{user?.name ?? "Usuario"}</div>
                  <small className="text-muted">{title}</small>
                </div>
              </button>
              <div className={`dropdown-menu dropdown-menu-end ${profileOpen ? "show" : ""}`}>
                <Link className="dropdown-item" to="/perfil">
                  Ajustar perfil (email/telefone)
                </Link>
                <Link className="dropdown-item" to="/perfil/senha">
                  Alterar senha
                </Link>
                <button className="dropdown-item" onClick={logout}>
                  Sair
                </button>
              </div>
            </div>
          </div>
        </header>
        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
};
